import React, { useEffect, useRef, useState } from "react";
import { StyledSessionWrapper, StyledSessionBody } from "../session.style";
import styled from "@emotion/styled";
import { VirtuosoMessageListLicense, VirtuosoMessageList } from "@virtuoso.dev/message-list";
import type { ItemLocation, VirtuosoMessageListMethods } from "@virtuoso.dev/message-list";
import { SessionItem, SessionRenderingContext } from "../session-item/session-item.utils";
import { SessionItemRenderer } from "@/mds/components/sessions/session-item/session-item-renderer";
import { MDSSessionContextValue } from "@/mds/components/sessions/session-context";
import { useSessionItemsWithUnreadDivider, useSessionRenderContext } from "./session-list.utils";
import { useCaughtUpNearBottom } from "./session-list-caught-up";
import { SessionListJumpToLatest } from "./session-list-jump-to-latest";
import { SessionEventKind } from "@/mds/components/sessions/session.types";
import { findLast, isEqual, last } from "lodash-es";

type Props = Pick<
  MDSSessionContextValue,
  "sessionStatus" | "activeArtifact" | "sessionConfig" | "sessionViewMode"
> & {
  sessionItems: SessionItem[];
  sessionId: string;
  containerHeight: number | string;
  /**
   * When true, disables the top fade gradient applied via `&::before`.
   * Defaults to false (top fade enabled).
   */
  disableTopFade?: boolean;
};

const NEAR_TOP_THRESHOLD_PX = 80;
/* Far enough from the bottom that the pill is worth offering — deliberately
   past the 100px near-bottom mark so it doesn't flicker in as the list settles. */
const JUMP_TO_LATEST_THRESHOLD_PX = 160;
const TOP_START_SCROLL_PADDING_PX = 16;

// Internal component that uses the context
export const MDSSessionList: React.FC<Props> = ({
  sessionConfig,
  sessionStatus,
  sessionItems: renderedSessionItems,
  sessionId,
  sessionViewMode,
  containerHeight,
  disableTopFade,
}) => {
  // The "New" divider rides along with the real items so it virtualizes,
  // scrolls, and keys like any other row.
  const sessionItems = useSessionItemsWithUnreadDivider({
    sessionItems: renderedSessionItems,
    unreadDividerEventId: sessionConfig.unreadDividerEventId,
    isUnreadDividerRetired: sessionConfig.isUnreadDividerRetired,
  });
  const messagesRef =
    useRef<VirtuosoMessageListMethods<SessionItem, SessionRenderingContext>>(null);
  const [hasInitiallyRendered, setHasInitiallyRendered] = useState(false);
  const prevSessionItemsRef = useRef<SessionItem[]>([]);
  const prevSessionIdRef = useRef<string>(sessionId);
  const wasNearTopRef = useRef(false);
  const shouldStartAtScrollTop = sessionConfig.initialScrollPosition === "top";
  const { reportBottomOffset } = useCaughtUpNearBottom({
    onCaughtUp: sessionConfig.onCaughtUpNearBottom,
  });
  // Separate from the caught-up latch above: that one fires once and stops,
  // while the pill has to come and go with every scroll.
  const [isScrolledFromBottom, setIsScrolledFromBottom] = useState(false);

  // Update the virtualized message list when session items change
  useEffect(() => {
    if (messagesRef.current && sessionItems.length > 0) {
      const isInitialRender = !hasInitiallyRendered;
      const hasSessionIdChanged = prevSessionIdRef.current !== sessionId;

      const bottomOffset = messagesRef?.current?.getScrollLocation().bottomOffset || 0;
      const isNearBottom = bottomOffset < 100;

      /**
       * If we are re-rendering, but the number of items hasn't changed, we don't
       * trigger auto-scrolling.
       *
       * We have one exception, which is if the final item has been modified in
       * some way (this can happen if a part is being updated by the backend, or
       * if it flips from "loading" to "placeholder", etc.)
       */
      // A message the user just sent always pops the list down, even from a
      // scrolled-up position; the near-bottom rule only applies to items the
      // user didn't author. Compared by identity of the last user-authored
      // item because sending also appends the synthetic status/loading item
      // after the user message, so the plain last item is never theirs.
      const lastUserAuthoredItemId = (items: SessionItem[]): string | undefined =>
        findLast(items, item => item.data[0]?.context.eventKind === SessionEventKind.UserActionV1)
          ?.id;
      const currentUserItemId = lastUserAuthoredItemId(sessionItems);
      // A session switch also changes the last user item's identity but must
      // keep its instant jump-to-bottom, not a smooth scroll through history.
      const hasNewUserMessage =
        !isInitialRender &&
        !hasSessionIdChanged &&
        currentUserItemId !== undefined &&
        currentUserItemId !== lastUserAuthoredItemId(prevSessionItemsRef.current);

      const shouldAutoScroll = (() => {
        if (isInitialRender) {
          return true;
        }

        if (hasNewUserMessage) {
          return true;
        }

        if (prevSessionItemsRef.current.length === sessionItems.length) {
          const previousLastItem = last(prevSessionItemsRef.current);
          const currentLastItem = last(sessionItems);

          if (isEqual(previousLastItem, currentLastItem)) {
            return false;
          }
        }

        return isNearBottom;
      })();

      if (!sessionId) return;
      // Use smooth scroll only when session ID changes, otherwise use instant
      const scrollBehavior = hasSessionIdChanged ? "instant" : customSmoothScroll;

      // The very first render can land on a specific event (peek-bubble tap)
      // instead of the bottom. Encoded as the initial location — a post-hoc
      // scrollToItem would be cancelled by the next data.replace of the
      // mount-time churn (status/summaries updates).
      const initialLocation: ItemLocation | undefined = (() => {
        const targetEventId = sessionConfig.initialScrollToEventId;
        if (isInitialRender && targetEventId) {
          const targetIndex = sessionItems.findIndex(item =>
            item.data.some(part => part.context.eventId === targetEventId)
          );
          if (targetIndex !== -1) {
            // Not "center": a message taller than the viewport would otherwise
            // land mid-message with its beginning above the fold. Not "start"
            // either — that pads the list bottom so the target can always reach
            // the top, leaving dead space under a near-last message. The
            // "no-overflow" variant tops it out as far as the content allows.
            return {
              index: targetIndex,
              align: "start-no-overflow" as const,
              behavior: "instant" as const,
            };
          }
        }

        if (isInitialRender && shouldStartAtScrollTop) {
          // Keep the scroll container at its true top so visual top padding
          // remains visible instead of becoming the initial scroll offset.
          return undefined;
        }

        return {
          index: "LAST" as const,
          align: "end" as const,
          behavior: scrollBehavior,
          offset: 100,
        };
      })();

      messagesRef.current.data.replace(sessionItems, {
        initialLocation: shouldAutoScroll ? initialLocation : undefined,
      });

      if (hasNewUserMessage) {
        // replace()'s initialLocation positions a fresh data set but does not
        // reliably move an already-mounted, scrolled-up list — scroll it.
        messagesRef.current.scrollToItem({ index: "LAST", align: "end", behavior: "smooth" });
      }

      if (isInitialRender) {
        setHasInitiallyRendered(true);
      }
    }

    // Update the refs for next comparison
    prevSessionItemsRef.current = sessionItems;
    prevSessionIdRef.current = sessionId;
  }, [
    hasInitiallyRendered,
    sessionConfig.initialScrollToEventId,
    sessionItems,
    sessionId,
    shouldStartAtScrollTop,
  ]);

  // A list that opens already at the bottom never fires onScroll, so the
  // caught-up wait has to be armed once the first items land.
  useEffect(() => {
    if (!sessionConfig.onCaughtUpNearBottom) return;
    if (!hasInitiallyRendered) return;
    const location = messagesRef.current?.getScrollLocation();
    if (location) reportBottomOffset(location.bottomOffset);
  }, [hasInitiallyRendered, reportBottomOffset, sessionConfig.onCaughtUpNearBottom]);

  const context = useSessionRenderContext({
    sessionStatus,
    sessionViewMode,
    sessionItems,
    sessionConfig,
  });

  return (
    <StyledSessionWrapper data-mds-name="MDSSessionList">
      {/* Session Body */}
      <StyledSessionBody data-component="StyledSessionBody">
        {/* Session Messages */}
        <MessagesContainer disableTopFade={disableTopFade} data-component="MessagesContainer">
          <VirtuosoMessageListLicense licenseKey={import.meta.env.VITE_VIRTUOSO_KEY}>
            <VirtuosoMessageList
              ref={messagesRef}
              ItemContent={SessionItemRenderer}
              context={context}
              initialData={sessionItems}
              computeItemKey={params => `${params.data.kind}:${params.data.id}`}
              style={{
                boxSizing: "border-box",
                height: containerHeight,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehaviorY: "contain",
                paddingLeft: 20,
                paddingRight: 20,
                paddingTop: shouldStartAtScrollTop ? TOP_START_SCROLL_PADDING_PX : 0,
                transition: "height 0.2s ease",
              }}
              shortSizeAlign={shouldStartAtScrollTop ? undefined : "bottom"}
              Header={shouldStartAtScrollTop ? undefined : SessionListHeader}
              Footer={SessionListFooter}
              onScroll={() => {
                // Since this a virtualized list, items can be destroyed on scroll
                // to help gracefully offload, we should invoke onMemItemOut
                sessionConfig.onMemItemOut?.();

                const location = messagesRef.current?.getScrollLocation();

                if (sessionConfig.onCaughtUpNearBottom && location) {
                  reportBottomOffset(location.bottomOffset);
                }

                if (sessionConfig.showJumpToLatest && location) {
                  setIsScrolledFromBottom(
                    location.bottomOffset >= JUMP_TO_LATEST_THRESHOLD_PX
                  );
                }

                if (sessionConfig.onScrolledNearTop) {
                  // listOffset is 0 at the very top and negative when
                  // scrolled down; edge-trigger so a page loads once per
                  // approach instead of on every scroll tick.
                  const location = messagesRef.current?.getScrollLocation();
                  const isNearTop =
                    location !== undefined && location.listOffset > -NEAR_TOP_THRESHOLD_PX;
                  if (isNearTop && !wasNearTopRef.current) {
                    sessionConfig.onScrolledNearTop();
                  }
                  wasNearTopRef.current = isNearTop;
                }
              }}
            />
          </VirtuosoMessageListLicense>
          {sessionConfig.showJumpToLatest && isScrolledFromBottom && (
            <SessionListJumpToLatest
              unreadCount={sessionConfig.unreadCount}
              onPress={() => {
                messagesRef.current?.scrollToItem({
                  index: "LAST",
                  align: "end",
                  behavior: "smooth",
                });
                setIsScrolledFromBottom(false);
              }}
            />
          )}
        </MessagesContainer>
      </StyledSessionBody>
    </StyledSessionWrapper>
  );
};

const MessagesContainer = styled("div", {
  // Avoid forwarding styling prop to the DOM
  shouldForwardProp: prop => prop !== "disableTopFade",
})<{
  disableTopFade?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  /* Hide scrollbars */
  & [data-virtuoso-scroller] {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;

const customSmoothScroll = () => {
  const easeOutQuad = (x: number): number => 1 - (1 - x) * (1 - x);

  return {
    // Increase the animation frame count to smoothen and slow down the scroll.
    animationFrameCount: 10,
    easing: easeOutQuad,
  };
};

const SessionListHeader = () => {
  return <StyledSessionListHeader />;
};

const StyledSessionListHeader = styled.div`
  display: block;
  height: ${({ theme }) => theme.spacing["4"]};
`;

const SessionListFooter = () => {
  return <StyledSessionListFooter />;
};

const StyledSessionListFooter = styled.div`
  display: block;
  height: ${({ theme }) => theme.spacing["0.5"]};
`;
